import formatUSD from '../../../../utils/formatUSD';

// Pose ids chosen for retouching, keyed by retouching product id. Levels are
// nested (Advanced includes Premium), so a photo sits on at most one of them:
// buying two levels for one photo would charge twice for the same work.
export type RetouchingSelections = Record<number, number[]>;

export const setPhotoLevel = (
  selections: RetouchingSelections,
  poseId: number,
  productId: number | null,
): RetouchingSelections => {
  const next: RetouchingSelections = {};
  Object.entries(selections).forEach(([id, poseIds]) => {
    next[Number(id)] = poseIds.filter((selectedId) => selectedId !== poseId);
  });
  if (productId !== null) {
    next[productId] = [...(next[productId] || []), poseId];
  }
  return next;
};

export const levelForPhoto = (
  selections: RetouchingSelections,
  productIds: number[],
  poseId: number,
): number | null =>
  productIds.find((productId) =>
    (selections[productId] || []).includes(poseId),
  ) ?? null;

// Tapping the photo itself is the quick path: it adds the photo at the
// default (first-listed) level, or takes it off whatever level it is on.
export const togglePhoto = (
  selections: RetouchingSelections,
  poseId: number,
  defaultProductId: number,
): RetouchingSelections => {
  const selected = Object.values(selections).some((poseIds) =>
    poseIds.includes(poseId),
  );
  return setPhotoLevel(selections, poseId, selected ? null : defaultProductId);
};

export const exclusiveSelections = (
  selections: RetouchingSelections,
  productOrder: number[],
): RetouchingSelections => {
  const claimed = new Set<number>();
  const next: RetouchingSelections = {};
  productOrder.forEach((productId) => {
    if (!selections[productId]) return;
    next[productId] = selections[productId].filter((poseId) => {
      if (claimed.has(poseId)) return false;
      claimed.add(poseId);
      return true;
    });
  });
  return next;
};

// The final price in a schedule repeats for every further image.
export const tieredPriceText = (
  basePriceCents: number,
  scheduleCents: number[],
): string => {
  const schedule = scheduleCents.length ? scheduleCents : [basePriceCents];
  const [first, ...rest] = schedule.map((cents) => formatUSD(cents));
  if (rest.length === 0) return `${first} per image`;
  if (rest.length === 1)
    return `${first} for the first image, ${rest[0]} for each additional image`;
  return `${first} for the first image, then ${rest.join(
    ', then ',
  )} for each additional image`;
};
